import { cn, Popover, PopoverAnchor, PopoverContent } from '@newfront-insurance/core-ui/v2';
import { ChevronDown, ChevronUp, ClipboardPaste, FileText, GitCompare, Search, Shield, Upload } from 'lucide-react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SlashCommand } from '../../../../queries/chats';

// Map slash command categories to icons
const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  GET: Search,
  FILL: FileText,
  REQUEST: Upload,
  INGEST: ClipboardPaste,
  CREATE: FileText,
  COMPARE: GitCompare,
  AUDIT: Shield,
};

// Map categories to display names
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  GET: 'Get',
  FILL: 'Fill',
  REQUEST: 'Request',
  INGEST: 'Ingest',
  CREATE: 'Create',
  COMPARE: 'Compare',
  AUDIT: 'Audit',
};

interface SlashCommandMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (command: SlashCommand) => void;
  commands: SlashCommand[];
  searchQuery: string;
  anchorRef: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
}

interface SlashCommandGroup {
  category: string;
  commands: SlashCommand[];
}

export function SlashCommandMenu({
  isOpen,
  onClose,
  onSelect,
  commands,
  searchQuery,
  anchorRef,
}: SlashCommandMenuProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedCommandUuid, setExpandedCommandUuid] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Filter commands based on search query
  const filteredCommands = useMemo(() => {
    if (!searchQuery || searchQuery === '/') {
      return commands;
    }

    const normalizedQuery = searchQuery.toLowerCase().replace(/^\//, '');
    return commands.filter(
      (command) =>
        command.handle.toLowerCase().includes(normalizedQuery) ||
        command.description.toLowerCase().includes(normalizedQuery),
    );
  }, [commands, searchQuery]);

  // Group filtered commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, SlashCommand[]> = {};

    filteredCommands.forEach((command) => {
      const { category } = command;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(command);
    });

    return Object.entries(groups).map(
      ([category, categoryCommands]): SlashCommandGroup => ({
        category,
        commands: categoryCommands,
      }),
    );
  }, [filteredCommands]);

  // Flatten commands for keyboard navigation
  const flatCommands = useMemo(() => filteredCommands, [filteredCommands]);

  // Reset selected index and collapse expanded command when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
    setExpandedCommandUuid(null);
  }, [filteredCommands.length]);

  // Toggle expanded state for a command
  const toggleExpanded = useCallback((commandUuid: string) => {
    setExpandedCommandUuid((prev) => (prev === commandUuid ? null : commandUuid));
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    const selectedItem = itemRefs.current.get(selectedIndex);
    if (selectedItem && listRef.current) {
      selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % flatCommands.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + flatCommands.length) % flatCommands.length);
          break;
        case 'Tab':
        case 'Enter':
          event.preventDefault();
          if (flatCommands[selectedIndex]) {
            onSelect(flatCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    },
    [isOpen, flatCommands, selectedIndex, onSelect, onClose],
  );

  // Attach keyboard listener to editor element (textarea or contenteditable div)
  useEffect(() => {
    const editor = anchorRef.current;
    if (!editor || !isOpen) return;

    editor.addEventListener('keydown', handleKeyDown as EventListener);
    return () => {
      editor.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [anchorRef, isOpen, handleKeyDown]);

  if (!isOpen || flatCommands.length === 0) {
    return null;
  }

  let itemIndex = 0;

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor asChild>
        <span className="pointer-events-none absolute bottom-full left-0" />
      </PopoverAnchor>
      <PopoverContent
        className="border-steel-200 w-[548px] overflow-hidden rounded-sm border bg-white p-0 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)]"
        side="top"
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="bg-steel-100 px-3.5 py-2.5">
          <span className="text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase">Available Commands</span>
        </div>

        {/* Command List */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto">
          {groupedCommands.map((group) => (
            <div key={group.category}>
              {/* Category Header */}
              <div className="bg-steel-50 flex items-center justify-between px-3.5 py-2.5">
                <span className="text-steel-500 text-sm font-semibold">
                  {CATEGORY_DISPLAY_NAMES[group.category] || group.category}
                </span>
                <span className="text-steel-400 text-sm">
                  {group.commands.length} {group.commands.length === 1 ? 'command' : 'commands'}
                </span>
              </div>

              {/* Commands in category */}
              {group.commands.map((command) => {
                const currentIndex = itemIndex;
                const isSelected = currentIndex === selectedIndex;
                const isExpanded = expandedCommandUuid === command.uuid;
                const Icon = CATEGORY_ICONS[command.category] || FileText;
                itemIndex += 1;

                return (
                  <SlashCommandItem
                    key={command.uuid}
                    ref={(el) => {
                      if (el) {
                        itemRefs.current.set(currentIndex, el);
                      } else {
                        itemRefs.current.delete(currentIndex);
                      }
                    }}
                    command={command}
                    icon={Icon}
                    isSelected={isSelected}
                    isExpanded={isExpanded}
                    onToggleExpand={() => toggleExpanded(command.uuid)}
                    onClick={() => onSelect(command)}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer - Keyboard Hints */}
        <div className="bg-steel-50 px-3.5 py-2.5">
          <span className="text-steel-400 text-sm">
            <kbd className="rounded px-1 py-0.5">↑↓</kbd> to navigate{' '}
            <kbd className="ml-2 rounded px-1 py-0.5">Tab</kbd>/<kbd className="rounded px-1 py-0.5">Enter</kbd> to
            select <kbd className="ml-2 rounded px-1 py-0.5">Esc</kbd> to cancel
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface SlashCommandItemProps {
  command: SlashCommand;
  icon: React.ComponentType<{ className?: string }>;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClick: () => void;
}

const SlashCommandItem = forwardRef<HTMLButtonElement, SlashCommandItemProps>(
  ({ command, icon: Icon, isSelected, isExpanded, onToggleExpand, onClick }, ref) => {
    return (
      <div
        className={cn(
          'flex w-full flex-col transition-colors',
          isExpanded ? 'bg-brand-100' : 'hover:bg-steel-50',
          isSelected && !isExpanded && 'bg-steel-100',
        )}
      >
        {/* Command Header Row */}
        <button
          ref={ref}
          type="button"
          className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left"
          onClick={onClick}
        >
          {/* Icon */}
          <div
            className={cn(
              'bg-brand-100 flex size-9 shrink-0 items-center justify-center rounded-sm',
              isExpanded ? 'border-steel-200 border' : '',
            )}
          >
            <Icon className="text-brand-400 size-[18px]" />
          </div>

          {/* Content */}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-steel-500 text-sm font-semibold">/{command.handle}</span>
            <span className="text-steel-400 truncate text-sm">{command.description}</span>
          </div>

          {/* Expand/Collapse Chevron */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="text-steel-400 hover:text-steel-500 shrink-0 p-1"
            aria-label={isExpanded ? 'Collapse command details' : 'Expand command details'}
          >
            {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </button>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="bg-steel-50 border-steel-200 mx-3.5 mb-2.5 flex flex-col gap-2.5 rounded-[5px] border p-2">
            {/* Full Prompt Section */}
            <div className="text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase">Full prompt</div>
            <div className="border-steel-200 rounded-[5px] border bg-white p-2">
              <p className="text-steel-400 text-sm leading-4 whitespace-pre-wrap">{command.prompt}</p>
            </div>

            {/* Example Usage Section */}
            {command.exampleUsage && (
              <>
                <div className="text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase">Example usage</div>
                <div className="border-steel-200 rounded-[5px] border bg-white p-2">
                  <p className="text-sm leading-4">
                    <span className="text-fire-400 font-mono">/{command.handle}</span>{' '}
                    <span className="text-steel-400">{command.exampleUsage}</span>
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

SlashCommandItem.displayName = 'SlashCommandItem';

// Hook for managing slash command menu state
export function useSlashCommandMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const open = useCallback((initialQuery = '/') => {
    setSearchQuery(initialQuery);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  const updateQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return {
    isOpen,
    searchQuery,
    open,
    close,
    updateQuery,
  };
}
